import Reveal from "@/components/Reveal";
import { channels } from "@/mocks/site";
import { hotRows } from "@/mocks/news";
import type { NewsItem } from "@/mocks/types";

interface HotRankProps {
  news: NewsItem[];
  onRead: (item: NewsItem) => void;
}

const trendMap = {
  up: { icon: "ri-arrow-up-line", cls: "text-primary-600", label: "上升" },
  down: { icon: "ri-arrow-down-line", cls: "text-secondary-500", label: "回落" },
  new: { icon: "ri-sparkling-2-fill", cls: "text-accent-600", label: "新上榜" },
  flat: { icon: "ri-loader-4-line", cls: "text-foreground-400", label: "持平" },
};

export default function HotRank({ news, onRead }: HotRankProps) {
  const max = Math.max(...news.map((n) => n.heat));
  const rows = hotRows
    .map((r) => {
      const item = news.find((n) => n.id === r.id);
      return item ? { ...r, item } : null;
    })
    .filter((x): x is NonNullable<typeof x> => x !== null);

  return (
    <section id="hot" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-[1400px] px-4 md:px-6">
        <Reveal>
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.24em] text-accent-600">
                <span className="h-px w-8 bg-accent-400"></span> Hot · 热度飙升榜
              </p>
              <h2 className="mt-3 font-heading text-2xl font-black tracking-tight text-foreground-900 md:text-[34px]">
                大家都在吃的瓜
              </h2>
            </div>
            <p className="text-sm text-foreground-500">按讨论热度实时排序 · 每 30 分钟刷新一次</p>
          </div>
        </Reveal>

        <div className="mt-8 grid gap-3 md:grid-cols-2">
          {rows.map((row, i) => {
            const ch = channels.find((c) => c.key === row.item.channel);
            const top3 = i < 3;
            const pct = Math.round((row.item.heat / max) * 100);
            return (
              <Reveal key={row.id} delay={(i % 2) * 70}>
                <button
                  type="button"
                  onClick={() => onRead(row.item)}
                  className="group flex w-full items-start gap-4 rounded-2xl border border-background-200 bg-background-50 p-4 text-left transition-all hover:-translate-y-0.5 hover:border-accent-200 hover:bg-background-100"
                >
                  <span
                    className={`font-heading text-[26px] font-black leading-none tracking-tight ${
                      top3 ? "text-primary-600" : "text-foreground-300"
                    }`}
                  >
                    {row.num}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="flex items-center gap-2">
                      <span
                        className={`whitespace-nowrap rounded-md px-1.5 py-0.5 text-[11px] font-bold ${
                          top3 ? "bg-primary-100 text-primary-800" : "bg-background-200 text-foreground-600"
                        }`}
                      >
                        {ch?.name}
                      </span>
                      <span
                        className={`inline-flex items-center gap-0.5 whitespace-nowrap text-[11px] font-semibold ${trendMap[row.trend].cls}`}
                      >
                        <i className={`${trendMap[row.trend].icon} text-xs`} aria-hidden="true"></i>
                        {row.delta}
                      </span>
                    </span>
                    <span className="mt-1.5 text-[15px] font-semibold leading-snug text-foreground-800 line-clamp-2 transition-colors group-hover:text-foreground-900">
                      {row.item.title}
                    </span>
                    <span className="mt-2.5 flex items-center gap-2 text-xs text-foreground-400">
                      <i className="ri-fire-fill text-accent-500" aria-hidden="true"></i>
                      {(row.item.heat / 1000).toFixed(1)}k 热度
                      <span className="relative ml-1 h-1.5 flex-1 overflow-hidden rounded-full bg-background-200">
                        <span
                          className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-accent-400 to-primary-500"
                          style={{ width: `${pct}%` }}
                        ></span>
                      </span>
                    </span>
                  </span>
                </button>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}